import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatImageUrl } from '../utils/imageUtils';
import {
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface Session {
  id: string;
  title: string;
  description: string;
  type: string;
  speakers: { 
    id: string; 
    name: string; 
    display_order: number;
    image?: string;
    company?: string;
  }[];
  startTime: string;
  endTime: string;
  room: string;
  status: string;
}

interface FeaturedSessionsProps {
  sessions: Session[];
  maxSessions?: number;
  showViewAllLink?: boolean;
  showHeader?: boolean;
}

const FeaturedSessions: React.FC<FeaturedSessionsProps> = ({ 
  sessions, 
  maxSessions = 4,
  showViewAllLink = true,
  showHeader = true
}) => {
  if (sessions.length === 0) {
    return null;
  }

  // Memoize random selection to prevent reshuffling on re-renders
  const displayedSessions = useMemo(() => {
    // Filter sessions to only include those with descriptions longer than 15 words
    const filteredSessions = sessions.filter(session => {
      const wordCount = session.description.trim().split(/\s+/).length;
      return wordCount > 15;
    });
    
    if (!showViewAllLink) return filteredSessions;
    
    if (filteredSessions.length <= maxSessions) return filteredSessions;
    
    const shuffled = [...filteredSessions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, maxSessions);
  }, [sessions, maxSessions, showViewAllLink]);

  const truncateText = (text: string, wordLimit: number = 30): string => {
    const words = text.split(' ');
    if (words.length <= wordLimit) {
      return text;
    }
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const formatTime = (timeString: string): string => {
    return new Date(timeString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (timeString: string): string => {
    return new Date(timeString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getSessionTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      keynote: 'bg-purple-100 text-purple-800',
      workshop: 'bg-blue-100 text-blue-800',
      presentation: 'bg-green-100 text-green-800',
      panel: 'bg-orange-100 text-orange-800',
      talk: 'bg-indigo-100 text-indigo-800',
      default: 'bg-gray-100 text-gray-800'
    };
    return colors[type.toLowerCase()] || colors.default;
  };

  return (
    <>
      <section className="mb-16">
        {showHeader && (
          <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
            Featured Sessions
          </h2>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {displayedSessions.map((session) => (
            <div 
              key={session.id} 
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
            >
              <div className="flex items-start justify-between mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSessionTypeColor(session.type)}`}>
                  {session.type}
                </span>
                <div className="text-right text-sm text-gray-500">
                  <div className="flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {formatDate(session.startTime)}
                  </div>
                  <div className="flex items-center mt-1">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {formatTime(session.startTime)} - {formatTime(session.endTime)}
                  </div>
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-3 text-[#00529B] line-clamp-2">{session.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{truncateText(session.description)}</p>
                
                {/* Speaker Information */}
                {session.speakers && session.speakers.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <UserIcon className="w-4 h-4 mr-1" />
                      {session.speakers.length === 1 ? 'Speaker' : 'Speakers'}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {session.speakers
                        .sort((a, b) => a.display_order - b.display_order)
                        .slice(0, 2) // Show max 2 speakers to avoid overcrowding
                        .map(speaker => (
                          <div key={speaker.id} className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
                            <div className="flex-shrink-0">
                              <img
                                src={formatImageUrl(speaker.image, "/placeholder-speaker.svg")}
                                alt={speaker.name}
                                className="w-8 h-8 rounded-full object-cover border border-gray-200"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {speaker.name}
                              </p>
                              {speaker.company && (
                                <p className="text-xs text-gray-500 truncate">
                                  {speaker.company}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      {session.speakers.length > 2 && (
                        <div className="flex items-center text-xs text-gray-500 bg-gray-50 rounded-lg px-2 py-1">
                          +{session.speakers.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100 mt-auto">
                <div className="flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  <span>Room {session.room}</span>
                </div>
                <Link 
                  to="/schedule" 
                  className="text-xs text-[#00529B] font-medium hover:text-[#003D74] transition-colors duration-200"
                >
                  View in schedule
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        {/* View All Sessions Link */}
        {showViewAllLink && sessions.length > maxSessions && (
          <div className="text-center mt-8">
            <Link
              to="/schedule"
              className="inline-flex items-center px-6 py-3 bg-[#00529B] text-white font-medium rounded-lg hover:bg-[#003D74] transition-colors duration-300"
            >
              See All Sessions ({sessions.length})
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        )}
      </section>
    </>
  );
};

export default FeaturedSessions;
